import type { EarningsEvent } from '../types/earnings'
import { sessionDayKey } from '../utils/sessionDay'
import { earningsBarIndex } from '../utils/earningsMarkersIndicator'

/**
 * The earnings event whose BADGE sits on the hovered bar.
 *
 * Matching by session day alone was wrong on intraday timeframes: a day holds
 * dozens of bars, so the card appeared anywhere in the session and covered a large
 * part of the chart, while the badge itself is pinned to a single bar. On a daily
 * chart the two happened to coincide — one bar per day — which is why it only
 * showed up intraday.
 *
 * The badge's bar is resolved with `earningsBarIndex`, the SAME function the
 * marker draw uses, so the hover target and the drawn badge cannot drift apart.
 *
 * Pure so it can be unit-tested without a chart (and housed in its own .ts
 * module so EarningsTooltip.tsx exports only components — react-refresh).
 * Returns `null` unless the hovered bar is exactly the badge's bar.
 */
export function matchEarnings(
  earnings: EarningsEvent[] | undefined,
  dataList: Array<{ timestamp: number } | undefined>,
  dataIndex: number,
  tz: string,
): EarningsEvent | null {
  return matchChartEvent(earnings, dataList, dataIndex, tz)
}

/**
 * Generic form of {@link matchEarnings} for any date-keyed chart event drawn
 * with the `earningsBarIndex` badge placement (earnings, splits, future
 * event markers): returns the event whose badge sits on the hovered bar, or
 * null. Same day-bucketing + first-bar-of-day rule, so hover targets and
 * drawn badges cannot drift apart across marker kinds.
 */
export function matchChartEvent<T extends { date: string }>(
  events: T[] | undefined,
  dataList: Array<{ timestamp: number } | undefined>,
  dataIndex: number,
  tz: string,
): T | null {
  if (!events || events.length === 0) return null
  const bar = dataList[dataIndex]
  if (!bar) return null
  const day = sessionDayKey(bar.timestamp, tz)
  const ev = events.find((e) => e.date === day)
  if (!ev) return null
  return earningsBarIndex(dataList, ev.date, tz) === dataIndex ? ev : null
}
